import { Linkedin, MousePointer, TrendingUp, Users } from 'lucide-react';
import { AreaChart } from '@tremor/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { EmptyState } from '@/components/charts/EmptyState';
import { MotionCard } from '@/components/motion/MotionCard';
import { useLinkedinDaily, useLinkedinSnapshot } from '@/hooks/useLinkedin';
import { formatNumberCompact } from '@/lib/format';

export function LinkedinPage() {
  const snapshot = useLinkedinSnapshot();
  const daily = useLinkedinDaily();

  const rows = daily.data ?? [];
  const totalImpressions = rows.reduce((s, r) => s + (r.impressions ?? 0), 0);
  const totalClicks = rows.reduce((s, r) => s + (r.clicks ?? 0), 0);
  const activeDays = rows.filter((r) => (r.impressions ?? 0) > 0).length;
  const ctrPct =
    totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

  const loading = snapshot.isLoading || daily.isLoading;
  const dateRange = rows.length > 0 ? `${rows[0]?.date} – ${rows[rows.length - 1]?.date}` : '';

  return (
    <div className="space-y-8">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <Linkedin className="h-5 w-5 text-sky-600" />
            LinkedIn — Flax Page
          </span>
        }
        description={dateRange ? `Brand-level performance · ${dateRange}` : 'Brand-level performance'}
      />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <SummaryTile
          label="Followers"
          value={
            snapshot.data?.followers_count != null
              ? formatNumberCompact(snapshot.data.followers_count)
              : '—'
          }
          icon={<Users className="h-3.5 w-3.5" />}
          loading={loading}
        />
        <SummaryTile
          label="Impressions (28d)"
          value={formatNumberCompact(totalImpressions)}
          icon={<TrendingUp className="h-3.5 w-3.5" />}
          loading={loading}
        />
        <SummaryTile
          label="Clicks (28d)"
          value={formatNumberCompact(totalClicks)}
          icon={<MousePointer className="h-3.5 w-3.5" />}
          loading={loading}
        />
        <SummaryTile
          label="CTR"
          value={ctrPct > 0 ? `${ctrPct.toFixed(2)}%` : '—'}
          icon={<TrendingUp className="h-3.5 w-3.5" />}
          loading={loading}
        />
      </div>

      <MotionCard>
        <Card>
          <CardHeader>
            <CardTitle>Daily impressions</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : rows.length === 0 ? (
              <EmptyState title="No data" description="No LinkedIn page data for this period." />
            ) : (
              <AreaChart
                data={rows.map((r) => ({
                  date: new Date(r.date).toLocaleDateString('en-IN', {
                    month: 'short',
                    day: 'numeric',
                  }),
                  impressions: r.impressions ?? 0,
                }))}
                index="date"
                categories={['impressions']}
                colors={['sky']}
                valueFormatter={formatNumberCompact}
                showGridLines={false}
                showLegend={false}
                showAnimation
                curveType="monotone"
                className="h-64"
              />
            )}
          </CardContent>
        </Card>
      </MotionCard>

      <MotionCard delay={0.05}>
        <Card>
          <CardHeader>
            <CardTitle>Activity summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              Flax's LinkedIn Page is currently <span className="font-medium text-foreground">low-activity</span>
              {' '}({activeDays} of {rows.length || 28} days with any impressions in this period). No active ad
              campaigns logged in Flax Ad account during May.
            </p>
            <p className="text-2xs">
              LinkedIn is typically B2B-leaning; for a B2C healthy-food brand it under-performs vs Instagram
              and aggregators. If corporate catering, hiring, or B2B partnerships become a priority, this is
              where to invest. Until then, treat this module as a passive monitor.
            </p>
          </CardContent>
        </Card>
      </MotionCard>
    </div>
  );
}

function SummaryTile({
  label,
  value,
  icon,
  loading,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-1.5 text-2xs font-normal text-muted-foreground">
          {icon}
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-20" />
        ) : (
          <div className="font-semibold tabular-nums text-2xl">{value}</div>
        )}
      </CardContent>
    </Card>
  );
}
